import { useCallback, useRef, useEffect } from "react";

type Section = "topshop" | "topman";

export const useTouchInteraction = (
  setHovered: (section: Section | null) => void,
  handleClick: (section: Section) => void,
  clicked: Section | null,
  isNavigating: boolean,
  containerRef: React.RefObject<HTMLDivElement>,
) => {
  const lastTouchedRef = useRef<Section | null>(null);
  const isTouch = useRef(false);
  const touchStartY = useRef<number | null>(null);
  const wasSwiping = useRef(false);

  useEffect(() => {
    const preventScroll = (e: TouchEvent | WheelEvent) => {
      e.preventDefault();
    };

    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("touchmove", preventScroll, { passive: false });
    container.addEventListener("wheel", preventScroll, { passive: false });

    return () => {
      container.removeEventListener("touchmove", preventScroll);
      container.removeEventListener("wheel", preventScroll);
    };
  }, [containerRef]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>, section: Section) => {
      isTouch.current = true;
      wasSwiping.current = false;
      touchStartY.current = e.touches[0]!.clientY;

      if (clicked || isNavigating) return;
      lastTouchedRef.current = section;
    },
    [clicked, isNavigating],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      isTouch.current = false;

      if (clicked || isNavigating) {
        touchStartY.current = null;
        return;
      }

      const endY = e.changedTouches[0]!.clientY ?? null;
      const startY = touchStartY.current;

      if (startY === null || endY === null) return;

      const deltaY = startY - endY;
      const isSwipe = Math.abs(deltaY) >= 30;

      if (isSwipe) {
        wasSwiping.current = true;

        const newSection: Section = deltaY > 0 ? "topman" : "topshop";
        lastTouchedRef.current = newSection;
        setHovered(newSection);
      } else {
        const tappedSection = lastTouchedRef.current;
        if (tappedSection && !clicked && !isNavigating) {
          handleClick(tappedSection);
        }
      }

      touchStartY.current = null;
    },
    [clicked, isNavigating, setHovered, handleClick],
  );

  const handleClickWithTouch = useCallback(
    (section: Section) => {
      if (!isTouch.current && !wasSwiping.current) {
        handleClick(section);
      }
      isTouch.current = false;
      wasSwiping.current = false;
    },
    [handleClick],
  );

  return {
    handleTouchStart,
    handleTouchEnd,
    handleClickWithTouch,
  };
};
